/**
 * Keyword highlight hook — computes per-line highlight ranges.
 *
 * Takes a list of highlight options (keyword + color) and returns
 * a function that computes the highlight ranges for a given line's text.
 * Used for watch-list / search highlighting (not spotlights — those
 * are handled by useLineSpotlight at the line level).
 */

import {useCallback} from 'react';
import {HighlightRange, HighlightOption} from './types';

export function useLineHighlight(options: HighlightOption[]) {
    /** Get highlight ranges for a single line's text */
    const getHighlightRanges = useCallback((text: string): HighlightRange[] => {
        if (!options.length || !text) return [];
        const ranges: HighlightRange[] = [];
        const lowerText = text.toLowerCase();
        for (const opt of options) {
            const needle = opt.label.toLowerCase();
            if (!needle) continue;
            let idx = lowerText.indexOf(needle);
            while (idx !== -1) {
                ranges.push({start: idx, end: idx + needle.length, color: opt.color});
                idx = lowerText.indexOf(needle, idx + 1);
            }
        }
        return ranges.sort((a, b) => a.start - b.start);
    }, [options]);

    return {getHighlightRanges};
}
